import {
  Attendance,
  AttendanceStatus,
  LocationType,
  Shift,
  ShiftTimingType,
  Signup,
  WaitlistEntry,
  WaitlistStatus,
} from '@amzn/red-velvet-api';
import {
  ShiftAction,
  ShiftStatus,
  UserShiftStatus,
  VolunteerShiftRow,
} from './volunteerEventShiftsTableTypes';

/**
 * Pure business logic for the VolunteerEventShiftsTable. No React, no side effects — every function
 * is a deterministic transform of its inputs so the column rendering, action selection, and default
 * ordering can be unit-tested in isolation.
 *
 * Deadline state is read from `shift.signupsOpen` — the backend-computed boolean
 * (RedVelvetApiTypescriptLambda shiftHelper.areShiftSignupsOpen, derived from event.signupDeadline at
 * read time). Using it directly keeps the FE's Sign Up vs Join Waitlist decision identical to what the
 * backend accepts, with no duplicated deadline math, and matches existing filterUtils / signupUtils usage.
 */

/** Parse a Timestamp (Date from the client, or ISO string defensively) to epoch ms, or undefined. */
export function toEpochMs(value: Date | string | undefined): number | undefined {
  if (!value) return undefined;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(ms) ? undefined : ms;
}

/**
 * Has the signup deadline passed? Reads the backend-computed shift.signupsOpen (false => deadline
 * passed). signupsOpen is always populated by toModelShift; an undefined value is treated as still
 * open (not past), so a shift is never pushed to the waitlist on missing data.
 */
export function isSignupDeadlinePast(shift: Shift): boolean {
  return shift.signupsOpen === false;
}

/** A shift is past once the backend flags it complete or its end time has elapsed. */
export function isShiftPast(shift: Shift, now: number = Date.now()): boolean {
  const end = toEpochMs(shift.endDateTime);
  return shift.complete === true || (end !== undefined && now > end);
}

/** Unlimited capacity (maxSignup 0/undefined) is never full; otherwise full when no spots remain. */
export function isShiftFull(shift: Shift): boolean {
  return !!shift.maxSignup && shift.maxSignup > 0 && shift.signupsRemaining <= 0;
}

/**
 * Whether the user holds a committed place in a shift. Every state that descends from a signup counts:
 * SIGNED_UP, plus the past-shift states MISSING_ATTENDANCE / ATTENDED / DID_NOT_ATTEND (see getUserStatus's
 * hierarchy) — a shift the volunteer signed up for and later attended is still their commitment.
 */
export function isCommittedToShift(userStatus: UserShiftStatus): boolean {
  return (
    userStatus === UserShiftStatus.SIGNED_UP ||
    userStatus === UserShiftStatus.MISSING_ATTENDANCE ||
    userStatus === UserShiftStatus.ATTENDED ||
    userStatus === UserShiftStatus.DID_NOT_ATTEND
  );
}

/**
 * Whether a row's cells should render dimmed. A full shift is greyed out to signal it can't be joined
 * — but a volunteer already committed to that shift keeps their row at full contrast, since it is
 * still their commitment. So dim only when the shift is full AND the user is not committed.
 */
export function isRowDimmed(row: VolunteerShiftRow): boolean {
  return isShiftFull(row.shift) && !isCommittedToShift(row.userStatus);
}

/**
 * Whether this shift accepts waitlist signups. Undefined means disabled: a pre-existing shift that
 * predates the ShiftWaitlist rollout carries no value and must not offer a waitlist. Only shifts
 * created with the flag on carry an explicit `waitlistEnabled: true`.
 */
export function isWaitlistEnabled(shift: Shift): boolean {
  return shift.waitlistEnabled === true;
}

/**
 * Lifecycle status for the Shift Status column. Order of checks matters: a started/ended shift is
 * Past or In Progress regardless of its deadline; only a not-yet-started shift can be Deadline Past.
 */
export function getShiftStatus(shift: Shift, now: number = Date.now()): ShiftStatus {
  if (isShiftPast(shift, now)) {
    return ShiftStatus.PAST;
  }
  const start = toEpochMs(shift.startDateTime);
  if (start !== undefined && now >= start) {
    // Started but neither ended nor complete (covers ongoing shifts that have a start but no end).
    return ShiftStatus.IN_PROGRESS;
  }
  if (isSignupDeadlinePast(shift)) {
    return ShiftStatus.DEADLINE_PAST;
  }
  return ShiftStatus.UPCOMING;
}

/**
 * The user's ACTIVE waitlist entry for a shift, if any. Shared by getUserStatus (membership) and
 * buildShiftRow (which carries it for the Leave action) so the two predicates never drift apart.
 */
function findActiveWaitlistEntry(
  shift: Shift,
  waitlistEntries: WaitlistEntry[] | undefined
): WaitlistEntry | undefined {
  return (waitlistEntries ?? []).find(
    (entry) => entry.shiftId === shift.shiftId && entry.status === WaitlistStatus.ACTIVE
  );
}

/**
 * The current user's status for a shift. An attendance record is the final truth, so it wins:
 * ATTENDED / DID_NOT_ATTEND > MISSING_ATTENDANCE (signed up for a past shift with no record yet) >
 * SIGNED_UP > ON_THE_WAITLIST (an ACTIVE waitlist entry) > NOT_SIGNED_UP.
 *
 * `signups`/`attendances`/`waitlistEntries` must already be scoped to the current user; this only
 * matches them to the shift by id. Empty waitlistEntries simply means ON_THE_WAITLIST never resolves.
 */
export function getUserStatus(
  shift: Shift,
  signups: Signup[],
  waitlistEntries: WaitlistEntry[] | undefined,
  attendances: Attendance[] | undefined,
  now: number = Date.now()
): UserShiftStatus {
  const attendance = (attendances ?? []).find((record) => record.shiftId === shift.shiftId);
  if (attendance?.attendanceStatus === AttendanceStatus.ATTENDED) {
    return UserShiftStatus.ATTENDED;
  }
  if (attendance?.attendanceStatus === AttendanceStatus.DID_NOT_ATTEND) {
    return UserShiftStatus.DID_NOT_ATTEND;
  }

  const isSignedUp = signups.some((signup) => signup.shiftId === shift.shiftId);
  const isOnWaitlist = findActiveWaitlistEntry(shift, waitlistEntries) !== undefined;

  if (isSignedUp && isShiftPast(shift, now)) {
    return UserShiftStatus.MISSING_ATTENDANCE;
  }
  if (isSignedUp) {
    return UserShiftStatus.SIGNED_UP;
  }
  if (isOnWaitlist) {
    return UserShiftStatus.ON_THE_WAITLIST;
  }
  return UserShiftStatus.NOT_SIGNED_UP;
}

/**
 * The single action a row offers, mirroring backend eligibility (joinWaitlist.assertEligibleToWaitlist
 * + createSignup deadline check):
 *  - On the waitlist                                 -> Leave Waitlist
 *  - Attended / Did not attend / Missing attendance  -> Track Time (control labels it Track/Edit)
 *  - Past + not signed up                            -> Track Time (retroactive)
 *  - Signed up (not past)                            -> Cancel sign up
 *  - Upcoming + spots open                           -> Sign Up
 *  - Not started + waitlist on + (full OR deadline past) -> Join Waitlist
 *  - otherwise                                       -> none (disabled)
 */
export function getShiftAction(
  shift: Shift,
  userStatus: UserShiftStatus,
  shiftStatus: ShiftStatus,
  isShiftWaitlistEnabled = false
): ShiftAction {
  if (userStatus === UserShiftStatus.ON_THE_WAITLIST) {
    return ShiftAction.LEAVE_WAITLIST;
  }
  // Any attendance state (recorded or missing) offers the attendance action; the action control picks
  // the label (Track vs Edit) and the modal (attendance vs volunteer-time) from the record + signup.
  if (
    userStatus === UserShiftStatus.MISSING_ATTENDANCE ||
    userStatus === UserShiftStatus.ATTENDED ||
    userStatus === UserShiftStatus.DID_NOT_ATTEND
  ) {
    return ShiftAction.TRACK_TIME;
  }
  if (shiftStatus === ShiftStatus.PAST) {
    return userStatus === UserShiftStatus.NOT_SIGNED_UP ? ShiftAction.TRACK_TIME : ShiftAction.NONE;
  }

  // cce-7837: a FLEXIBLE shift stays actionable while IN_PROGRESS — its multi-day window is still
  // open (a scheduled shift falls through to the NONE guard once started). In-progress shifts do
  // NOT offer the waitlist: signups remain open, so a not-signed-up volunteer routes to Sign Up (a
  // full shift or a passed deadline instead routes to retroactive Track attendance — attendance
  // recording is not capacity-gated).
  // Gated on the ShiftWaitlist feature flag: with the flag off, an in-progress shift falls through
  // to the legacy NONE guard, matching the backend (signupsOpen stays closed for in-progress
  // FLEXIBLE shifts when the flag is off), so the feature does not leak ahead of its rollout.
  if (
    isShiftWaitlistEnabled &&
    shiftStatus === ShiftStatus.IN_PROGRESS &&
    shift.shiftTimingType === ShiftTimingType.FLEXIBLE
  ) {
    // Already signed up, no time tracked yet (attendance states were handled above): kebab.
    if (userStatus === UserShiftStatus.SIGNED_UP) {
      return ShiftAction.CANCEL_OR_TRACK;
    }
    // Not signed up: a shift at capacity or past its signup deadline can't take a new signup, but
    // attendance recording is neither capacity- nor deadline-gated, so a volunteer who did the work
    // can still retroactively Track attendance (cce-7837). Only an open, in-window shift signs up.
    if (isShiftFull(shift) || isSignupDeadlinePast(shift)) {
      return ShiftAction.TRACK_TIME;
    }
    return ShiftAction.SIGN_UP;
  }

  if (userStatus === UserShiftStatus.SIGNED_UP) {
    return ShiftAction.CANCEL_SIGNUP;
  }

  // Sign Up / Join Waitlist only apply to a not-yet-started shift. An IN_PROGRESS shift has already
  // passed its signup deadline, and the backend rejects both a signup (deadline) and a waitlist join
  // (joinWaitlist.assertEligibleToWaitlist requires isShiftUpcoming), so offer no action.
  if (shiftStatus !== ShiftStatus.UPCOMING && shiftStatus !== ShiftStatus.DEADLINE_PAST) {
    return ShiftAction.NONE;
  }
  const isDeadlinePast = shiftStatus === ShiftStatus.DEADLINE_PAST;
  if (!isShiftFull(shift) && !isDeadlinePast) {
    return ShiftAction.SIGN_UP;
  }
  if (isWaitlistEnabled(shift) && (isShiftFull(shift) || isDeadlinePast)) {
    return ShiftAction.JOIN_WAITLIST;
  }
  return ShiftAction.NONE;
}

/** Build a fully-resolved row (status + action), carrying the user's matched signup/attendance. */
export function buildShiftRow(
  shift: Shift,
  signups: Signup[],
  waitlistEntries: WaitlistEntry[] | undefined,
  attendances: Attendance[] | undefined,
  now: number = Date.now(),
  isShiftWaitlistEnabled = false
): VolunteerShiftRow {
  const shiftStatus = getShiftStatus(shift, now);
  const userStatus = getUserStatus(shift, signups, waitlistEntries, attendances, now);
  const action = getShiftAction(shift, userStatus, shiftStatus, isShiftWaitlistEnabled);
  const signup = signups.find((s) => s.shiftId === shift.shiftId);
  const attendance = (attendances ?? []).find((a) => a.shiftId === shift.shiftId);
  const waitlistEntry = findActiveWaitlistEntry(shift, waitlistEntries);
  return { shift, shiftStatus, userStatus, action, signup, attendance, waitlistEntry };
}

// Default ordering weights (cce-7841 / cce-4614). "Actionable for me" rows float up: Missing
// Attendance -> Signed Up -> On Waitlist are their own priority groups. Every other status
// (Attended, Did Not Attend, Not Signed Up) collapses into a single catch-all group, which is then
// sub-ordered by shift lifecycle. Only the catch-all group is lifecycle-sub-ordered; the top three
// groups sort purely by start time.
const CATCH_ALL_USER_STATUS_GROUP = 3;
const USER_STATUS_GROUP: Record<UserShiftStatus, number> = {
  [UserShiftStatus.MISSING_ATTENDANCE]: 0,
  [UserShiftStatus.SIGNED_UP]: 1,
  [UserShiftStatus.ON_THE_WAITLIST]: 2,
  [UserShiftStatus.ATTENDED]: CATCH_ALL_USER_STATUS_GROUP,
  [UserShiftStatus.DID_NOT_ATTEND]: CATCH_ALL_USER_STATUS_GROUP,
  [UserShiftStatus.NOT_SIGNED_UP]: CATCH_ALL_USER_STATUS_GROUP,
};

const SHIFT_STATUS_ORDER: Record<ShiftStatus, number> = {
  [ShiftStatus.IN_PROGRESS]: 0,
  [ShiftStatus.UPCOMING]: 1,
  [ShiftStatus.DEADLINE_PAST]: 2,
  [ShiftStatus.PAST]: 3,
};

/**
 * Default row ordering: group by user status (Missing Attendance -> Signed Up -> On Waitlist ->
 * everything else); within the "everything else" group order by lifecycle (In Progress -> Upcoming
 * -> Deadline Past -> Past); finally by start time ascending (undefined starts sort last). Pure and
 * non-mutating (operates on a copy).
 */
export function sortShiftRows(rows: VolunteerShiftRow[]): VolunteerShiftRow[] {
  return [...rows].sort((a, b) => {
    const groupDelta = USER_STATUS_GROUP[a.userStatus] - USER_STATUS_GROUP[b.userStatus];
    if (groupDelta !== 0) return groupDelta;

    // Lifecycle ordering (In Progress -> Upcoming -> Deadline Past -> Past) applies only inside the
    // catch-all group (Attended / Did Not Attend / Not Signed Up). The Missing Attendance / Signed
    // Up / On Waitlist groups sort purely by start time. a and b share a group here (groupDelta 0).
    if (USER_STATUS_GROUP[a.userStatus] === CATCH_ALL_USER_STATUS_GROUP) {
      const statusDelta = SHIFT_STATUS_ORDER[a.shiftStatus] - SHIFT_STATUS_ORDER[b.shiftStatus];
      if (statusDelta !== 0) return statusDelta;
    }

    const aStart = toEpochMs(a.shift.startDateTime) ?? Number.POSITIVE_INFINITY;
    const bStart = toEpochMs(b.shift.startDateTime) ?? Number.POSITIVE_INFINITY;
    return aStart - bStart;
  });
}

// --- Column sort support (the table's sortable header carets) ---

// Ascending sort order for the Shift Status column (lifecycle). Distinct from USER_STATUS_GROUP /
// SHIFT_STATUS_ORDER above, which drive the default row grouping; these orders apply only when the
// user clicks a column header to sort.
const SHIFT_STATUS_SORT_ORDER: Record<ShiftStatus, number> = {
  [ShiftStatus.UPCOMING]: 0,
  [ShiftStatus.IN_PROGRESS]: 1,
  [ShiftStatus.DEADLINE_PAST]: 2,
  [ShiftStatus.PAST]: 3,
};

// Ascending sort order for the Your Status column when the user sorts by it: by engagement level
// (Attended, Signed Up, On Waitlist, Missing Attendance, Did Not Attend, Not Signed Up). Deliberately
// distinct from getUserStatus's urgency order and the default-grouping USER_STATUS_GROUP; this is only
// the column's display sort, not row prioritization.
const USER_STATUS_SORT_ORDER: Record<UserShiftStatus, number> = {
  [UserShiftStatus.ATTENDED]: 0,
  [UserShiftStatus.SIGNED_UP]: 1,
  [UserShiftStatus.ON_THE_WAITLIST]: 2,
  [UserShiftStatus.MISSING_ATTENDANCE]: 3,
  [UserShiftStatus.DID_NOT_ATTEND]: 4,
  [UserShiftStatus.NOT_SIGNED_UP]: 5,
};

/**
 * Resolve a location object to its displayed label: the virtual label for a virtual location,
 * otherwise the first populated of amazonBuilding / city / name / address, otherwise the fallback.
 * Shared by the event-level label, the per-shift Location cell, and the Location sort comparator so
 * all three stay in sync.
 */
export function resolveLocationLabel(
  location: Shift['location'] | undefined,
  virtualLabel: string,
  fallback: string
): string {
  if (location?.locationType === LocationType.VIRTUAL) return virtualLabel;
  return (
    location?.amazonBuilding ||
    location?.city ||
    location?.name ||
    location?.address_string ||
    fallback
  );
}

/**
 * The shift's displayed location label, falling back to the event's. A per-shift location applies only
 * when the shift opts out of the event location (useEventLocation === false).
 */
export function getShiftLocationLabel(
  shift: Shift,
  eventLocationLabel: string,
  virtualLabel: string
): string {
  const location = shift.useEventLocation === false ? shift.location : undefined;
  return resolveLocationLabel(location, virtualLabel, eventLocationLabel);
}

/**
 * Per-column ascending sort comparators keyed by column id, for the table's sortable headers. The
 * Action column is intentionally omitted (an action control is not a meaningful sort key), so it
 * renders no caret. Location depends on the event-derived labels, so the caller passes them in.
 */
export function getVolunteerShiftSortComparators(
  eventLocationLabel: string,
  virtualLabel: string
): Record<string, (a: VolunteerShiftRow, b: VolunteerShiftRow) => number> {
  const byStartTime = (a: VolunteerShiftRow, b: VolunteerShiftRow) =>
    (toEpochMs(a.shift.startDateTime) ?? Number.POSITIVE_INFINITY) -
    (toEpochMs(b.shift.startDateTime) ?? Number.POSITIVE_INFINITY);

  return {
    shiftName: (a, b) => (a.shift.name ?? '').localeCompare(b.shift.name ?? ''),
    shiftStatus: (a, b) =>
      SHIFT_STATUS_SORT_ORDER[a.shiftStatus] - SHIFT_STATUS_SORT_ORDER[b.shiftStatus],
    location: (a, b) =>
      getShiftLocationLabel(a.shift, eventLocationLabel, virtualLabel).localeCompare(
        getShiftLocationLabel(b.shift, eventLocationLabel, virtualLabel)
      ),
    date: byStartTime,
    time: byStartTime,
    yourStatus: (a, b) =>
      USER_STATUS_SORT_ORDER[a.userStatus] - USER_STATUS_SORT_ORDER[b.userStatus],
  };
}
